import {
  Box,
  Button,
  Divider,
  Popover,
  SxProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import ImageWrapper from '../../elements/ImageWrapper';
import { IconWrapper } from '../../elements/IconWrapper/Index';
import { theme } from '../../../theme/themeProvider';
import {
  getMediaBasePath,
  firstLetterImage,
} from '../../../commonUtils/images';
import {
  AssessmentEnum,
  MarkingSchemeEnum,
  SubmissionType,
  TaskCreationStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import { remainingTimeInHoursAndMins } from '../../../commonUtils/homework';
import { ScoreProgressBar } from '../ScoreProgressBar';
import { useState } from 'react';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import {
  getLocalStorage,
  formatRemainingTime,
} from 'libs/shared-ui/src/commonUtils/utilFunctions';
import { Timestamp } from '@bufbuild/protobuf';
import { SubjectChapterTopicPopup } from './components/SubjectChapterTopicPopup';

const styles: IStyles = {
  root: {
    height: { xs: pxToRem(148), md: pxTovW(280), lg: pxTovW(199) },
    width: { xs: '100%', md: pxTovW(590), lg: pxTovW(464) },
    borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0DFDE',
    padding: {
      xs: pxToRem(8),
      md: `${pxTovW(18)} ${pxTovW(15)}`,
      lg: `${pxTovW(13)} ${pxTovW(12)}`,
    },
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(15), md: pxTovW(15), lg: pxTovW(23) },
    boxSizing: 'border-box',
    boxShadow: `0px 0px ${pxToRem(11)} #E7E7E7D9`,
    marginX: 'auto',
    cursor: 'pointer',
  },
  imageBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
    height: { xs: '100%', md: '100%', lg: pxTovW(157) },
    width: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(157) },
    minWidth: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(157) },
    boxSizing: 'border-box',
    borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
    backgroundColor: '#E0DFDE',
    position: 'relative',
  },
  image: {
    width: '100%',
    objectFit: 'cover',
    borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
  },
  iconBox: {
    display: 'flex',
    gap: { xs: pxToRem(2), md: pxTovW(2) },
    alignItems: 'center',
    width: 'max-content',
  },
  progressContainer: {
    // border: '1px solid green',
    boxSizing: 'border-box',
    position: 'absolute',
    bgcolor: '#FFFFFFE6',
    width: '100%',
    height: { xs: pxToRem(39), md: pxTovW(53) },
    bottom: 0,
    opacity: '90%',
    borderRadius: {
      xs: `0 0 ${pxToRem(10)} ${pxToRem(10)}`,
      md: `0 0 ${pxTovW(10)} ${pxTovW(10)}`,
    },
    p: { xs: pxToRem(5), md: pxTovW(5) },
    justifyContent: 'center',
    display: 'flex',
    gap: { xs: pxToRem(2), md: pxTovW(2) },
    flexDirection: 'column',
  },
  BadgeContainer: {
    boxSizing: 'border-box',
    position: 'absolute',
    width: '100%',
    height: { xs: pxToRem(39), md: pxTovW(53) },
    top: { xs: pxToRem(16), md: pxTovW(20) },
  },
  publishStatusContainer: {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'left',
    padding: { xs: pxToRem(5), md: pxTovW(5) },
    alignItems: 'center',
    position: 'absolute',
    width: { xs: pxToRem(70), md: pxToRem(60), lg: pxTovW(90) },
    borderBottomRightRadius: { xs: pxToRem(10), md: pxTovW(15) },
    height: { xs: pxToRem(20), md: pxTovW(30) },
    top: 0,
  },
  popoverBox: {
    '& .MuiPopover-root': {},
    '& .MuiPopover-paper': {
      // width: { xs: pxTovW(277), md: pxTovW(183), lg: pxTovW(183) },
      borderRadius: { xs: pxToRem(10), md: pxToRem(10) },
    },
  },
};

export interface SubjectListItem {
  subjectName: string;
  id: number;
}

interface IProps {
  homework?: AssessmentTask;
  onClick?: () => void;
  assessCardType?: 'Draft' | 'Assigned' | 'Ended';
  chapterListPopupHandler?: (assessment: AssessmentTask) => void;
  onDeleteClick?: () => void;
  onCopyClick?: () => void;
  rootStyles?: SxProps;
  hideDeleteIcon?: boolean;
  modeofConduction?: boolean;
  proctoring?: boolean;
  subjectList?: SubjectListItem[];
}
export const AssessmentCard = (props: IProps) => {
  const {
    homework,
    onClick,
    assessCardType,
    chapterListPopupHandler,
    onDeleteClick,
    onCopyClick,
    rootStyles,
    hideDeleteIcon,
    modeofConduction,
    proctoring,
    subjectList,
  } = props;

  let rootSx = { ...styles.root };
  if (rootStyles) rootSx = { ...rootSx, ...rootStyles };
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const isIpadOnly = !isMobile && isIpad;
  const teacher_id = getLocalStorage('userId');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [subjectChapterTopicPopupOpen, setSubjectChapterTopicPopupOpen] =
    useState(false);

  // ---------------------------------------------------------

  function isEmpty(val: any) {
    return (
      val == null || // null or undefined
      (Array.isArray(val) && val.length === 0) ||
      (typeof val === 'object' &&
        !Array.isArray(val) &&
        Object.keys(val).length === 0)
    );
  }

  // ---------------------------------------------------------

  const assessmentNotStarted = () => {
    return (
      homework?.assessmentCommenceDate &&
      new Timestamp(homework.assessmentCommenceDate).toDate() > new Date()
    );
  };

  // ---------------------------------------------------------

  const getSubjectsArray = () => {
    if (!subjectList) return [];
    const subjects = new Set<string>();
    subjectList.forEach((subject) => subjects.add(subject.subjectName));
    return Array.from(subjects);
  };

  // ---------------------------------------------------------

  return (
    <>
      <Box
        sx={rootSx}
        onClick={onClick}
        data-marking-scheme={
          homework?.markingSchemeType === MarkingSchemeEnum.MARKING_SCHEME_GENEO
            ? 'geneo'
            : homework?.markingSchemeType ===
              MarkingSchemeEnum.MARKING_SCHEME_JEE
            ? 'jee'
            : 'unknown'
        }
      >
        <Box sx={styles.imageBox}>
          <ImageWrapper
            name="chapter1"
            type="png"
            parentFolder="tempAssets"
            styles={{
              ...styles.image,
              height: '100%',
            }}
            path={getMediaBasePath(
              homework?.assessmentPosterImgUrl,
              'processedMediaBucket'
            )}
          />
          {homework?.assessmentType ===
            AssessmentEnum.ASSESSMENT_QUESTION_PAPER ||
          homework?.taskType ===
            TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM ? (
            homework.taskCreationStatus ===
            TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED ? (
              <Box
                sx={{
                  ...styles.publishStatusContainer,
                  backgroundColor: '#B5FFD6',
                }}
              >
                <Typography variant="smallText" color={'#0AA34F'}>
                  Published
                </Typography>
              </Box>
            ) : homework.taskCreationStatus ===
              TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT ? (
              <Box
                sx={{
                  ...styles.publishStatusContainer,
                  backgroundColor: '#DDF0FF',
                }}
              >
                <Typography variant="smallText" color={'#007CDC'}>
                  Draft
                </Typography>
              </Box>
            ) : (
              <></>
            )
          ) : (
            <></>
          )}

          {modeofConduction ? (
            // <Box sx={styles.BadgeContainer}>
            //   <ImageWrapper
            //     name="clicker_tag"
            //     type="png"
            //     parentFolder="tempAssets"
            //     styles={{
            //       width: isIpadOnly
            //         ? pxTovW(160)
            //         : isMobile
            //         ? pxToRem(91)
            //         : pxTovW(139),
            //       height: isIpadOnly
            //         ? pxTovW(52)
            //         : isMobile
            //         ? pxToRem(29)
            //         : pxTovW(42),
            //       objectFit: 'fill',
            //       position: 'fit',
            //     }}
            //   />
            // </Box>

            <ImageBadge imageName="geneo_clicker_blue" />
          ) : proctoring ? (
            <ImageBadge imageName="yellow-proctored" />
          ) : null}

          {assessmentNotStarted() ? (
            <Box
              sx={{
                ...styles.progressContainer,
                backgroundColor: '#E6F3FF',
                opacity: '95%',
              }}
            >
              <Typography variant="subText" fontWeight="bold">
                Starts In
              </Typography>
              <Box sx={styles.iconBox}>
                <IconWrapper
                  name="clock"
                  size={isIpadOnly ? 'md' : 'small'}
                  parentFolder="icons"
                  type="png"
                />
                <Typography variant="subText" fontWeight="bold">
                  {formatRemainingTime(homework?.assessmentCommenceDate)}
                </Typography>
              </Box>
            </Box>
          ) : homework?.maxMarks !== undefined && assessCardType !== 'Draft' ? (
            <Box sx={styles.progressContainer}>
              <ScoreProgressBar
                variant={isIpadOnly ? 'md' : 'small'}
                score={Math.round(homework?.classScore || 0)}
              />
              <Typography
                variant="smallestText"
                sx={{ marginLeft: pxToRem(5) }}
              >
                Class Score
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          {assessCardType === 'Assigned' &&
            !hideDeleteIcon &&
            homework?.teacherName !== 'Geneo' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'flex-end',
                  position: 'absolute',
                  right: pxToRem(5),
                  top: pxToRem(0),
                }}
                onClick={(ev?: React.MouseEvent) => {
                  ev?.stopPropagation();
                  onDeleteClick && onDeleteClick(); // Open delete confirmation popup
                }}
              >
                <IconWrapper
                  name="deletehw"
                  size={isIpadOnly ? 'md' : 'small'}
                  parentFolder="icons"
                  type="png"
                  customSx={{
                    cursor: 'pointer',
                    width: {
                      xs: pxToRem(20),
                      md: pxTovW(30),
                      lg: pxTovW(25),
                    },
                    height: {
                      xs: pxToRem(20),
                      md: pxTovW(30),
                      lg: pxTovW(25),
                    },
                  }}
                />
              </Box>
            )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: { xs: pxToRem(3), md: pxTovW(4) },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: { xs: pxToRem(17), md: pxTovW(28) },
                gap: { xs: pxToRem(10), md: pxTovW(16) },
                marginTop: { xs: pxToRem(2), md: pxTovW(10), lg: pxTovW(5) },
                marginBottom: { xs: pxToRem(1), md: pxTovW(5) },
              }}
            >
              {
                // homework?.taskType ===
                // TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM ? (
                //   <Box
                //     sx={{
                //       display: 'flex',
                //       justifyContent: 'center',
                //       alignItems: 'center',
                //       // marginTop: { xs: pxToRem(5), md: pxTovW(10), lg: pxTovW(5) },
                //       background: '#F8C807',
                //       padding: {
                //         xs: `${pxToRem(3)} ${pxToRem(7)} ${pxToRem(4)}`,
                //         md: `${pxTovW(5)} ${pxTovW(14)} ${pxTovW(6)}`,
                //       },
                //       borderRadius: {
                //         xs: pxToRem(10),
                //         md: pxTovW(20),
                //         lg: pxTovW(16),
                //       },
                //     }}
                //   >
                //     <Typography
                //       variant={isMobile ? 'smallestText' : 'subText'}
                //       fontWeight={700}
                //     >
                //       Multi Subject Question Paper
                //     </Typography>
                //   </Box>
                // ) :
                homework?.taskType === TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      // marginTop: { xs: pxToRem(5), md: pxTovW(10), lg: pxTovW(5) },
                      background: '#F8C807',
                      padding: {
                        xs: `${pxToRem(3)} ${pxToRem(7)} ${pxToRem(4)}`,
                        md: `${pxTovW(5)} ${pxTovW(14)} ${pxTovW(6)}`,
                      },
                      borderRadius: {
                        xs: pxToRem(10),
                        md: pxTovW(20),
                        lg: pxTovW(16),
                      },
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'smallestText' : 'subText'}
                      fontWeight={700}
                    >
                      Custom Homework
                    </Typography>
                  </Box>
                ) : homework?.assessmentType ===
                  AssessmentEnum.ASSESSMENT_HOMEWORK ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      // marginTop: { xs: pxToRem(5), md: pxTovW(10), lg: pxTovW(5) },
                      background: '#0AA34F',
                      padding: {
                        xs: `${pxToRem(3)} ${pxToRem(7)} ${pxToRem(4)}`,
                        md: `${pxTovW(4)} ${pxTovW(10)} ${pxTovW(6)}`,
                        lg: `${pxTovW(5)} ${pxTovW(10)} ${pxTovW(6)}`,
                      },
                      borderRadius: {
                        xs: pxToRem(10),
                        md: pxTovW(20),
                        lg: pxTovW(15),
                      },
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'smallestText' : 'subText'}
                      color={'common.white'}
                      fontWeight={700}
                    >
                      Homework
                    </Typography>
                  </Box>
                ) : homework?.assessmentType ===
                  AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 'max-content',
                      background: '#007CDC',
                      padding: {
                        xs: `${pxToRem(3)} ${pxToRem(7)} ${pxToRem(4)}`,
                        md: `${pxTovW(4)} ${pxTovW(14)} ${pxTovW(7)}`,
                        lg: `${pxTovW(5)} ${pxTovW(14)} ${pxTovW(6)}`,
                      },
                      borderRadius: {
                        xs: pxToRem(10),
                        md: pxTovW(20),
                        lg: pxTovW(15),
                      },
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'smallestText' : 'subText'}
                      color={'common.white'}
                      fontWeight={700}
                    >
                      Formative
                    </Typography>
                  </Box>
                ) : homework?.assessmentType ===
                  AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      background: '#F87606',
                      padding: {
                        xs: `${pxToRem(3)} ${pxToRem(8)} ${pxToRem(4)}`,
                        md: `${pxTovW(5)} ${pxTovW(9)} ${pxTovW(6)}`,
                      },
                      borderRadius: {
                        xs: pxToRem(10),
                        md: pxTovW(20),
                        lg: pxTovW(15),
                      },
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'smallestText' : 'subText'}
                      color={'common.white'}
                      fontWeight={'700'}
                    >
                      Diagnostic
                    </Typography>
                  </Box>
                ) : (
                  homework?.assessmentType ===
                    AssessmentEnum.ASSESSMENT_QUESTION_PAPER && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: '#DC0093',
                        padding: {
                          xs: `${pxToRem(3)} ${pxToRem(8)} ${pxToRem(4)}`,
                          md: `${pxTovW(5)} ${pxTovW(9)} ${pxTovW(6)}`,
                        },
                        borderRadius: {
                          xs: pxToRem(10),
                          md: pxTovW(20),
                          lg: pxTovW(15),
                        },
                      }}
                    >
                      <Typography
                        variant={isMobile ? 'smallestText' : 'subText'}
                        color={'common.white'}
                        fontWeight={'700'}
                      >
                        Question Paper
                      </Typography>
                    </Box>
                  )
                )
              }
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: pxToRem(5), md: pxTovW(5) },
                }}
              >
                <ImageWrapper
                  name="geneo-blue"
                  type="png"
                  parentFolder="icons"
                  styles={{
                    width: { xs: pxToRem(17), md: pxTovW(34), lg: pxTovW(24) },
                    height: { xs: pxToRem(17), md: pxTovW(34), lg: pxTovW(24) },
                    borderRadius: '50%',
                  }}
                  path={
                    homework?.teacherProfileImageUrl
                      ? getMediaBasePath(
                          homework?.teacherProfileImageUrl,
                          'schoolnetCdnImagesBucket'
                        )
                      : firstLetterImage('Geneo')
                  }
                />
                <Typography
                  variant={isMobile ? 'smallestText' : 'smallText'}
                  fontWeight="regular"
                >
                  {homework?.teacherId?.toString()
                    ? 'You'
                    : homework?.teacherName || 'Geneo'}
                </Typography>
              </Box>
              {/* <Box>x</Box> */}
              {homework?.assessmentType ===
                AssessmentEnum.ASSESSMENT_QUESTION_PAPER &&
                homework?.teacherId?.toString() === teacher_id?.toString() &&
                (onDeleteClick || onCopyClick) && (
                  <>
                    <Button
                      sx={{ marginLeft: 'auto' }}
                      onClick={(ev) => {
                        ev?.stopPropagation();
                        setAnchorEl(ev.currentTarget);
                      }}
                    >
                      <IconWrapper
                        name="triple-horizontal-dots"
                        size="small"
                        parentFolder="icons"
                        type="png"
                        customSx={{
                          transform: 'rotate(90deg)',
                          height: '12px',
                        }}
                      />
                    </Button>
                  </>
                )}
            </Box>
            <Typography
              variant={isMobile ? 'smallestText' : 'subText'}
              fontWeight={700}
              color="primary.main"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
              }}
            >
              {homework?.class}
              {homework?.section} {homework?.subject}
              {homework?.taskType ===
                TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM ||
              homework?.taskType ===
                TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM
                ? '- ' + getSubjectsArray().join(', ')
                : ''}
            </Typography>
            <Typography
              variant={isMobile ? 'h4' : 'h5'}
              fontWeight={700}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
                maxWidth: {
                  xs: pxToRem(190),
                  md: pxTovW(320),
                  lg: pxTovW(220),
                },
              }}
            >
              {homework?.moduleName}
            </Typography>
            {(homework?.assessmentType ===
              AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT ||
              homework?.assessmentType ===
                AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT) &&
            !isEmpty(homework.taskMetaInfo?.chapterInfo?.chapterTopicsInfo) &&
            homework?.taskType !==
              TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM ? (
              <Box
                sx={{
                  display: 'flex',
                  // height: { xs: pxToRem(12), md: pxTovW(15) },
                  width: 'max-content',
                  gap: { xs: pxToRem(3), md: pxTovW(8), lg: pxTovW(5) },
                  cursor: 'pointer',
                }}
                onClick={(ev?: React.MouseEvent) => {
                  ev?.stopPropagation();
                  chapterListPopupHandler && chapterListPopupHandler(homework);
                }}
              >
                {homework.taskMetaInfo?.chapterInfo?.chapterTopicsInfo.length &&
                homework.taskMetaInfo?.chapterInfo?.chapterTopicsInfo.length >
                  1 ? (
                  <Typography
                    variant={isMobile ? 'smallestText' : 'subText'}
                    fontWeight="bold"
                  >
                    {`+${
                      homework.taskMetaInfo?.chapterInfo?.chapterTopicsInfo &&
                      homework.taskMetaInfo?.chapterInfo?.chapterTopicsInfo
                        .length - 1
                    } more`}
                  </Typography>
                ) : (
                  <></>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: {
                      xs: `0 ${pxToRem(6)} ${pxToRem(1)}`,
                      md: `${pxTovW(1)} ${pxTovW(10)} ${pxTovW(4)}`,
                      lg: `0 ${pxTovW(6)} ${pxTovW(1)}`,
                    },
                    background: 'black',
                    borderRadius: {
                      xs: pxToRem(10),
                      md: pxTovW(20),
                      lg: pxTovW(15),
                    },
                  }}
                >
                  <Typography variant="smallestText" color={'common.white'}>
                    i
                  </Typography>
                </Box>
              </Box>
            ) : (
              <></>
            )}
            <Typography
              variant={isMobile ? 'smallestText' : 'subText'}
              fontWeight={500}
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word',
                maxWidth: {
                  xs: pxToRem(190),
                  md: pxTovW(320),
                  lg: pxTovW(220),
                },
                // marginTop: { xs: pxToRem(4), md: pxTovW(2) },
              }}
            >
              {homework?.assessmentTitle}
            </Typography>

            {(homework?.taskType ===
              TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM ||
              homework?.taskType ===
                TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: pxToRem(5), md: pxTovW(5) },
                  mt: { xs: pxToRem(5), md: pxTovW(5) },
                }}
              >
                <Typography variant="subText" fontWeight="bold">
                  Details
                </Typography>
                <Button
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setSubjectChapterTopicPopupOpen(true);
                  }}
                  sx={{ padding: 0 }}
                >
                  <IconWrapper
                    name="tooltip-black"
                    parentFolder="icons"
                    type="png"
                    size="small"
                    customSx={{
                      height: {
                        xs: pxToRem(14),
                        md: pxTovW(20),
                        lg: pxTovW(14),
                      },
                      width: {
                        xs: pxToRem(14),
                        md: pxTovW(20),
                        lg: pxTovW(14),
                      },
                      cursor: 'pointer',
                    }}
                  />
                </Button>
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: '1px solid #E0E0E0' }} />

          <Box
            sx={{
              display: 'flex',
              boxSizing: 'border-box',
              justifyContent: 'space-between',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxSizing: 'border-box',
              }}
            >
              {homework?.taskType === TaskEnum.TASK_HOMEWORK_TEACHER_CUSTOM ? (
                <Box
                  sx={{
                    display: 'flex',
                    marginRight: 'auto',
                    gap: { xs: pxToRem(4), md: pxTovW(6) },
                    marginBottom: { xs: pxToRem(4), md: pxTovW(10) },
                    alignItems: 'center',
                  }}
                >
                  <IconWrapper
                    name="tnc"
                    size={isIpadOnly ? 'md' : 'small'}
                    parentFolder="icons"
                    type="png"
                  />
                  {homework?.maxMarks === undefined ? (
                    <Typography variant="smallText" fontWeight={900}>
                      {homework?.submissionType ===
                      SubmissionType.SUBMISSION_ONLINE
                        ? 'Digital without Marks'
                        : 'Physical without Marks'}
                    </Typography>
                  ) : (
                    <Typography variant="smallText" fontWeight={900}>
                      {homework?.submissionType ===
                      SubmissionType.SUBMISSION_ONLINE
                        ? 'Digital with Marks'
                        : 'Physical with Marks'}
                    </Typography>
                  )}
                </Box>
              ) : assessCardType === 'Draft' ? (
                <>
                  <Box sx={styles.iconBox}>
                    <IconWrapper
                      name="clock"
                      size={isIpadOnly ? 'md' : 'small'}
                      parentFolder="icons"
                      type="png"
                    />

                    <Typography variant={isMobile ? 'smallestText' : 'subText'}>
                      {`${homework?.assessmentContentInfo?.timeDurationInMin}mins`}
                    </Typography>
                  </Box>
                  <Box sx={styles.iconBox}>
                    <IconWrapper
                      name="questions"
                      size={isIpadOnly ? 'md' : 'small'}
                      parentFolder="icons"
                      type="png"
                    />
                    <Typography variant={isMobile ? 'smallestText' : 'subText'}>
                      {`${homework?.assessmentContentInfo?.numberOfQuestions}Qs`}
                    </Typography>
                  </Box>
                  <Box sx={styles.iconBox}>
                    <IconWrapper
                      name={
                        homework?.difficultyLevel === 1
                          ? 'difficulty-hard'
                          : homework?.difficultyLevel === 2
                          ? 'level'
                          : 'difficulty-easy'
                      }
                      size={isIpadOnly ? 'md' : 'small'}
                      parentFolder="icons"
                      type="png"
                    />
                    <Typography variant={isMobile ? 'smallestText' : 'subText'}>
                      {difficultyLevelToString(homework?.difficultyLevel || 0)}
                    </Typography>
                  </Box>
                </>
              ) : (
                // For assessments which are neither custom hw nor shown in the assessment page
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box sx={styles.iconBox}>
                      <IconWrapper
                        name="profile"
                        size={isIpadOnly ? 'md' : 'small'}
                        parentFolder="icons"
                        type="png"
                      />
                      <Typography variant="smallText">
                        {`${homework?.studentsSubmissionCount || 0}/${
                          homework?.assignedStudentsCount || 0
                        }`}
                      </Typography>
                    </Box>
                    <Typography variant="smallText" fontWeight="regular">
                      Submissions
                    </Typography>
                  </Box>
                  {assessCardType === 'Assigned' ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={styles.iconBox}>
                        <IconWrapper
                          name="clock"
                          size={isIpadOnly ? 'md' : 'small'}
                          parentFolder="icons"
                          type="png"
                        />
                        <Typography
                          variant="smallText"
                          sx={{
                            maxWidth: { md: pxTovW(85) },
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {remainingTimeInHoursAndMins(
                            homework?.assessmentTargetDate
                          )}
                        </Typography>
                      </Box>
                      <Typography variant="smallText" fontWeight="regular">
                        Remaining
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: '#E1FFEE',
                        border: '1px solid #0AA34F',
                        width: {
                          xs: pxToRem(65),
                          md: pxTovW(110),
                          lg: pxTovW(86),
                        },
                        height: {
                          xs: pxToRem(23),
                          md: pxTovW(40),
                          lg: pxTovW(31),
                        },
                        borderRadius: {
                          xs: pxToRem(10),
                          md: pxTovW(21),
                          lg: pxTovW(16),
                        },
                      }}
                    >
                      <Typography variant="smallText" color={'secondary.main'}>
                        Ended
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Popover
        id={'popover-assessment'}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={styles.popoverBox}
      >
        {homework?.taskCreationStatus ===
          TaskCreationStatusEnum.TASK_CREATION_STATUS_APPROVED && (
          <Box>
            <RenderPopoverButton
              label="Create Copy"
              onClick={(event) => {
                setAnchorEl(null);
                event.stopPropagation();
                onCopyClick && onCopyClick();
              }}
            />
          </Box>
        )}
        <Box>
          <RenderPopoverButton
            label="Delete"
            onClick={(event) => {
              setAnchorEl(null);
              event.stopPropagation();
              onDeleteClick && onDeleteClick();
            }}
          />
        </Box>
      </Popover>

      {(homework?.taskType ===
        TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM ||
        homework?.taskType ===
          TaskEnum.TASK_MULTI_SUBJECT_FORMATIVE_ASSESSMENT_CUSTOM) &&
      subjectChapterTopicPopupOpen ? (
        <SubjectChapterTopicPopup
          popupOpen={subjectChapterTopicPopupOpen}
          popupCloseHandler={() => setSubjectChapterTopicPopupOpen(false)}
          subjectList={subjectList}
          assessmentData={homework}
        />
      ) : null}
    </>
  );
};

// =====================================================

const difficultyLevelToString = (value: number) => {
  switch (value) {
    case 0:
      return 'Undefined';
      break;
    case 1:
      return 'Hard';
      break;
    case 2:
      return 'Medium';
      break;
    case 3:
      return 'Easy';
      break;

    default:
      break;
  }
};

// =====================================================

interface IRenderPopoverButton {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  label: string;
}

const RenderPopoverButton = ({ onClick, label }: IRenderPopoverButton) => {
  return (
    <Button onClick={(event) => onClick(event)} sx={styles.popOverItem}>
      <Typography variant="h4" fontWeight="medium">
        {label}
      </Typography>
    </Button>
  );
};

// =====================================================

const ImageBadge = ({ imageName }: { imageName: string }) => {
  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        position: 'absolute',
        top: { xs: pxToRem(0), md: pxTovW(0) },
      }}
    >
      <ImageWrapper
        name={imageName}
        type="png"
        parentFolder="tempAssets"
        styles={{
          // width: { xs: pxToRem(104), md: pxTovW(130), lg: pxTovW(104) },
          width: { xs: '70%', md: '70%', lg: '70%' },
          // height: { xs: pxToRem(37), md: pxTovW(45), lg: pxTovW(37) },
        }}
      />
    </Box>
  );
};

// =====================================================
